import { useState, useCallback, useRef, useLayoutEffect, useEffect } from 'react';
import { useTranslation } from '../../../i18n/TranslationContext';

interface CountryData {
  id: string;
  countryKey: string;
  cx: number;
  cy: number;
  labelX: number;
  labelY: number;
  labelAnchor: 'start' | 'end';
  line: { x1: number; y1: number; x2: number; y2: number };
  hitbox: { x: number; y: number; width: number; height: number };
  fill: string;
  hoverFill: string;
  textColor: string;
  stats: { value: string; labelKey: string }[];
}

// SVG viewBox="0 0 1009.6727 665.96301"
const countries: CountryData[] = [
  {
    id: 'CA',
    countryKey: 'brands.locations.canada',
    cx: 190,
    cy: 225,
    labelX: 120,
    labelY: 155,
    labelAnchor: 'end',
    line: { x1: 190, y1: 225, x2: 120, y2: 155 },
    hitbox: { x: 30, y: 30, width: 350, height: 180 },
    fill: '#e8967a',
    hoverFill: '#c8705a',
    textColor: '#4a1810',
    stats: [
      { value: '8', labelKey: 'brands.locations.stat_brands' },
      { value: '150+', labelKey: 'brands.locations.stat_creators' },
      { value: '32', labelKey: 'brands.locations.stat_campaigns' },
    ],
  },
  {
    id: 'US',
    countryKey: 'brands.locations.usa',
    cx: 215,
    cy: 315,
    labelX: 320,
    labelY: 335,
    labelAnchor: 'start',
    line: { x1: 215, y1: 315, x2: 320, y2: 335 },
    hitbox: { x: 60, y: 190, width: 320, height: 185 },
    fill: '#d4a86a',
    hoverFill: '#b08848',
    textColor: '#4a3010',
    stats: [
      { value: '15', labelKey: 'brands.locations.stat_brands' },
      { value: '420+', labelKey: 'brands.locations.stat_creators' },
      { value: '86', labelKey: 'brands.locations.stat_campaigns' },
    ],
  },
  {
    id: 'KR',
    countryKey: 'brands.locations.korea',
    cx: 837,
    cy: 352,
    labelX: 912,
    labelY: 295,
    labelAnchor: 'start',
    line: { x1: 837, y1: 352, x2: 912, y2: 295 },
    hitbox: { x: 820, y: 325, width: 60, height: 60 },
    fill: '#c96a80',
    hoverFill: '#a8485e',
    textColor: '#4a1020',
    stats: [
      { value: '6', labelKey: 'brands.locations.stat_brands' },
      { value: '200+', labelKey: 'brands.locations.stat_creators' },
      { value: '45', labelKey: 'brands.locations.stat_campaigns' },
    ],
  },
  {
    id: 'JP',
    countryKey: 'brands.locations.japan',
    cx: 878,
    cy: 348,
    labelX: 952,
    labelY: 348,
    labelAnchor: 'start',
    line: { x1: 878, y1: 348, x2: 952, y2: 348 },
    hitbox: { x: 856, y: 310, width: 65, height: 125 },
    fill: '#5a9688',
    hoverFill: '#3a7668',
    textColor: '#0a2a22',
    stats: [
      { value: '5', labelKey: 'brands.locations.stat_brands' },
      { value: '180+', labelKey: 'brands.locations.stat_creators' },
      { value: '38', labelKey: 'brands.locations.stat_campaigns' },
    ],
  },
  {
    id: 'CN',
    countryKey: 'brands.locations.china',
    cx: 735,
    cy: 325,
    labelX: 650,
    labelY: 285,
    labelAnchor: 'end',
    line: { x1: 735, y1: 325, x2: 650, y2: 285 },
    hitbox: { x: 640, y: 220, width: 215, height: 178 },
    fill: '#c85a4e',
    hoverFill: '#a03a30',
    textColor: '#4a1008',
    stats: [
      { value: '10', labelKey: 'brands.locations.stat_brands' },
      { value: '350+', labelKey: 'brands.locations.stat_creators' },
      { value: '72', labelKey: 'brands.locations.stat_campaigns' },
    ],
  },
  {
    id: 'europe-group',
    countryKey: 'brands.locations.europe',
    cx: 500,
    cy: 250,
    labelX: 420,
    labelY: 215,
    labelAnchor: 'end',
    line: { x1: 500, y1: 250, x2: 420, y2: 215 },
    hitbox: { x: 415, y: 105, width: 180, height: 185 },
    fill: '#a89078',
    hoverFill: '#887058',
    textColor: '#3a2210',
    stats: [
      { value: '12', labelKey: 'brands.locations.stat_brands' },
      { value: '280+', labelKey: 'brands.locations.stat_creators' },
      { value: '64', labelKey: 'brands.locations.stat_campaigns' },
    ],
  },
  {
    id: 'middle-east-group',
    countryKey: 'brands.locations.middle_east',
    cx: 620,
    cy: 390,
    labelX: 705,
    labelY: 425,
    labelAnchor: 'start',
    line: { x1: 620, y1: 390, x2: 705, y2: 425 },
    hitbox: { x: 565, y: 335, width: 155, height: 135 },
    fill: '#b48a6a',
    hoverFill: '#946a4a',
    textColor: '#3a1808',
    stats: [
      { value: '4', labelKey: 'brands.locations.stat_brands' },
      { value: '90+', labelKey: 'brands.locations.stat_creators' },
      { value: '22', labelKey: 'brands.locations.stat_campaigns' },
    ],
  },
];

export default function BrandLocations() {
  const { t } = useTranslation();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const mapRef = useRef<HTMLDivElement>(null);
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const [svgContent, setSvgContent] = useState('');

  useEffect(() => {
    const basePath = process.env.PUBLIC_URL || '';
    fetch(`${basePath}/worldmap.svg`)
      .then((res) => res.text())
      .then((text) => setSvgContent(text))
      .catch(() => setSvgContent(''));
  }, []);

  // Toggle `.country-glow` class on hover — fills are static CSS now, no JS fill manipulation
  useLayoutEffect(() => {
    const container = svgContainerRef.current;
    if (!container || !svgContent) return;

    // Remove glow class from all previously glowed elements
    container.querySelectorAll('.country-glow').forEach((el) => {
      el.classList.remove('country-glow');
    });

    if (!hoveredId) return;

    if (hoveredId === 'europe-group' || hoveredId === 'middle-east-group') {
      const group = container.querySelector(`g[id="${hoveredId}"]`);
      if (group) group.classList.add('country-glow');
    } else {
      const path = container.querySelector(`path[id="${hoveredId}"]`);
      if (path) path.classList.add('country-glow');
    }
  }, [hoveredId, svgContent]);

  const updatePos = useCallback((e: React.MouseEvent) => {
    const container = mapRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    setTooltipPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top + 14,
    });
  }, []);

  // Parent-level hover: find data-country from target, avoid null flickering
  const handleOverlayHover = useCallback((e: React.MouseEvent) => {
    const target = (e.target as Element).closest('[data-country]') as Element | null;
    const countryId = target?.getAttribute('data-country');
    if (countryId && countryId !== hoveredIdRef.current) {
      hoveredIdRef.current = countryId;
      setHoveredId(countryId);
    }
    updatePos(e);
  }, [updatePos]);

  const handleOverlayOut = useCallback((e: React.MouseEvent) => {
    const related = (e.relatedTarget as Element)?.closest('[data-country]');
    if (!related) {
      hoveredIdRef.current = null;
      setHoveredId(null);
    }
  }, []);

  const hoveredIdRef = useRef<string | null>(null);

  const visibleCountry = hoveredId ? countries.find((c) => c.id === hoveredId) : null;

  const scrollToCountryCard = useCallback((countryId: string) => {
    const el = document.getElementById(`country-card-${countryId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  const getDesc = (key: string): string[] => {
    const raw = t(`${key}_desc`);
    return raw
      .split(/,|，/)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const getLabelWidth = (countryKey: string) => {
    const name = t(countryKey);
    return Math.max(78, name.length * 8.5 + 28);
  };

  return (
    <section className="py-20 md:py-28 bg-secondary-50/40">
      <style>{`
        /* === Base: all paths transparent === */
        .map-svg-container path {
          fill: none !important;
          stroke: #2d2a26 !important;
          stroke-width: 0.5 !important;
          stroke-linejoin: round;
          pointer-events: none;
          cursor: default;
          transition: filter 0.35s ease;
        }

        /* === Brand country fills (static CSS — never lost) === */
        .map-svg-container path[id="CA"]  { fill: #e8967a !important; }
        .map-svg-container path[id="US"]  { fill: #d4a86a !important; }
        .map-svg-container path[id="KR"]  { fill: #c96a80 !important; }
        .map-svg-container path[id="JP"]  { fill: #5a9688 !important; }
        .map-svg-container path[id="CN"]  { fill: #c85a4e !important; }
        .map-svg-container g[id="europe-group"] path        { fill: #a89078 !important; }
        .map-svg-container g[id="middle-east-group"] path   { fill: #b48a6a !important; }

        /* === Pop-forward shadow on hover === */
        .map-svg-container path.country-glow,
        .map-svg-container g.country-glow path {
          filter: drop-shadow(0 6px 14px rgba(0,0,0,0.35));
        }

        .marker-ring {
          animation: markerPulse 2.4s ease-out infinite;
          transform-origin: center;
        }
        @keyframes markerPulse {
          0%   { r: 10; opacity: .5; stroke-width: 2.5; }
          100% { r: 28; opacity: 0;  stroke-width: .5; }
        }

        @keyframes markerFadeIn {
          from { opacity: 0; transform: scale(0.3); }
          to   { opacity: 1; transform: scale(1); }
        }
        .marker-enter {
          opacity: 0;
          animation: markerFadeIn 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        .marker-hit-area { fill: transparent; }
        .label-hit-area  { fill: transparent; cursor: pointer; }
        .label-pill  { pointer-events: none; }
        .label-text  { pointer-events: none; }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="mb-12 md:mb-16">
          <p className="text-xs font-medium uppercase tracking-widest text-primary-500 mb-4">
            {t('brands.locations.title')}
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-950 leading-tight max-w-3xl">
            {t('brands.locations.heading')}
          </h2>
          <p className="text-sm text-foreground-600 mt-4 max-w-xl leading-relaxed">
            {t('brands.locations.subtitle')}
          </p>
        </div>

        {/* Desktop map */}
        <div ref={mapRef} className="hidden lg:block relative w-full">
          <div className="relative w-full max-w-[1010px] mx-auto">
            {/* Inline SVG world map */}
            <div
              ref={svgContainerRef}
              dangerouslySetInnerHTML={{ __html: svgContent }}
              className="w-full select-none map-svg-container"
            />

            {/* Overlay: markers + labels + hitboxes */}
            <svg
              viewBox="0 0 1009.6727 665.96301"
              className="w-full absolute top-0 left-0 select-none pointer-events-none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ zIndex: 10 }}
            >
              <defs>
                <filter id="labelShadow2" x="-20%" y="-20%" width="140%" height="140%">
                  <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="#000000" floodOpacity="0.1" />
                </filter>
              </defs>

              <g
                style={{ pointerEvents: 'auto' }}
                onMouseOver={handleOverlayHover}
                onMouseMove={handleOverlayHover}
                onMouseOut={handleOverlayOut}
                onClick={(e) => {
                  const target = (e.target as Element).closest('[data-country]') as Element | null;
                  const countryId = target?.getAttribute('data-country');
                  if (countryId) scrollToCountryCard(countryId);
                }}
              >
                {countries.map((item, idx) => {
                  const isOn = item.id === hoveredId;
                  const r = isOn ? 7 : 5;
                  const labelW = getLabelWidth(item.countryKey);
                  const labelH = 28;
                  const rx = 14;
                  const pillX =
                    item.labelAnchor === 'start'
                      ? item.labelX - 8
                      : item.labelX - labelW + 8;
                  const textCenterX =
                    item.labelAnchor === 'start'
                      ? item.labelX - 8 + labelW / 2
                      : item.labelX - labelW + 8 + labelW / 2;

                  return (
                    <g key={item.id} className="marker-enter" style={{ animationDelay: `${idx * 0.12}s` }}>
                      {/* Connector line */}
                      <line
                        x1={item.line.x1}
                        y1={item.line.y1}
                        x2={item.line.x2}
                        y2={item.line.y2}
                        stroke={isOn ? item.textColor : '#9a9590'}
                        strokeWidth={isOn ? 1.5 : 1}
                        opacity={isOn ? 0.9 : 0.4}
                        strokeLinecap="round"
                        style={{ pointerEvents: 'none' }}
                      />

                      {/* Pulse ring */}
                      <circle
                        cx={item.cx}
                        cy={item.cy}
                        fill="none"
                        stroke={item.fill}
                        className="marker-ring"
                        style={{ pointerEvents: 'none' }}
                      />

                      {/* Invisible larger hit area around marker — no hover, just visual */}
                      <circle
                        cx={item.cx}
                        cy={item.cy}
                        r={22}
                        className="marker-hit-area"
                      />

                      {/* Center dot */}
                      <circle
                        cx={item.cx}
                        cy={item.cy}
                        r={r}
                        fill={isOn ? item.hoverFill : item.fill}
                        stroke="#ffffff"
                        strokeWidth="1.5"
                        style={{
                          filter: isOn ? `drop-shadow(0 0 6px ${item.fill})` : 'none',
                          pointerEvents: 'none',
                        }}
                      />

                      {/* Label pill background */}
                      <rect
                        x={pillX}
                        y={item.labelY - labelH / 2}
                        width={labelW}
                        height={labelH}
                        rx={rx}
                        fill="rgba(255,255,255,0.93)"
                        stroke={isOn ? item.fill : 'rgba(0,0,0,0.08)'}
                        strokeWidth={isOn ? 1.5 : 1}
                        className="label-pill"
                        filter="url(#labelShadow2)"
                      />

                      {/* Label hit area (invisible rect tracking the pill) */}
                      <rect
                        x={pillX - 4}
                        y={item.labelY - labelH / 2 - 4}
                        width={labelW + 8}
                        height={labelH + 8}
                        rx={rx + 4}
                        className="label-hit-area"
                        data-country={item.id}
                        style={{ cursor: 'pointer' }}
                      />

                      {/* Country name label */}
                      <text
                        x={textCenterX}
                        y={item.labelY}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="label-text select-none"
                        style={{
                          fontSize: isOn ? 14 : 12,
                          fontWeight: 700,
                          fill: item.textColor,
                          fontFamily: 'var(--font-label), sans-serif',
                        }}
                      >
                        {t(item.countryKey)}
                      </text>
                    </g>
                  );
                })}
              </g>
            </svg>
          </div>

          {/* Tooltip — hover only */}
          {visibleCountry && (
            <div
              className="absolute z-50 pointer-events-none"
              style={{
                left: tooltipPos.x,
                top: tooltipPos.y,
                transform: 'translate(-50%, 0)',
              }}
            >
              <div className="bg-background-50/95 backdrop-blur-sm border border-background-200/80 rounded-xl px-5 py-4 min-w-[270px] max-w-[340px]">
                <div className="flex items-center gap-2.5 mb-2.5">
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: visibleCountry.fill }}
                  />
                  <h4 className="text-sm font-bold text-foreground-950">
                    {t(visibleCountry.countryKey)}
                  </h4>
                </div>
                <ul className="space-y-1.5">
                  {getDesc(visibleCountry.countryKey).map((task, i) => (
                    <li
                      key={i}
                      className="text-xs text-foreground-600 leading-relaxed flex items-start gap-2"
                    >
                      <span className="mt-1.5 w-1 h-1 rounded-full bg-foreground-400 flex-shrink-0" />
                      <span>{task}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex justify-center">
                <div className="w-2.5 h-2.5 bg-background-50 border-r border-b border-background-200/80 rotate-45 -mt-1.5" />
              </div>
            </div>
          )}
        </div>

        {/* Desktop: country cards grid */}
        <div className="hidden lg:block mt-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
            {countries.map((item) => (
              <div
                key={item.id}
                id={`country-card-${item.id}`}
                className="group bg-background-50 border border-background-200/60 rounded-xl p-5 hover:border-background-300/80 transition-colors duration-300"
              >
                <div className="flex items-center gap-3 mb-3.5">
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ background: item.fill }}
                  />
                  <h4 className="text-sm font-bold text-foreground-950">
                    {t(item.countryKey)}
                  </h4>
                </div>
                {/* Stats row */}
                <div className="flex items-center gap-4 mb-3.5 pb-3.5 border-b border-background-200/50">
                  {item.stats.map((stat, si) => (
                    <div key={si} className="text-center flex-1">
                      <div className="text-lg font-bold text-foreground-950 leading-tight">
                        {stat.value}
                      </div>
                      <div className="text-[10px] text-foreground-500 mt-0.5 whitespace-nowrap">
                        {t(stat.labelKey)}
                      </div>
                    </div>
                  ))}
                </div>
                <ul className="space-y-2">
                  {getDesc(item.countryKey).map((task, i) => (
                    <li
                      key={i}
                      className="text-xs text-foreground-600 leading-relaxed flex items-start gap-2"
                    >
                      <span
                        className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0"
                        style={{ background: item.fill, opacity: 0.6 }}
                      />
                      <span>{task}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile: country cards */}
        <div className="lg:hidden flex flex-col gap-4">
          {countries.map((item) => (
            <div
              key={item.id}
              className="bg-background-50 border border-background-200/60 rounded-xl p-4"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ background: item.fill }}
                />
                <h4 className="text-sm font-bold text-foreground-950">
                  {t(item.countryKey)}
                </h4>
              </div>
              <ul className="space-y-1.5">
                {getDesc(item.countryKey).map((task, i) => (
                  <li
                    key={i}
                    className="text-xs text-foreground-600 leading-relaxed flex items-start gap-2"
                  >
                    <span className="mt-1.5 w-1 h-1 rounded-full bg-foreground-400 flex-shrink-0" />
                    <span>{task}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}